import type { ReactNode } from 'react'

import { cn } from '@/shared/lib'
import { StatusBadge } from '@/shared/ui/status-badge'

type RecordCardProps = {
  /** Mono identifier, top-left. */
  id: ReactNode
  status?: { tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral'; label: string }
  title: ReactNode
  /** Mono figure, bottom-right. */
  amount?: ReactNode
  onClick?: () => void
  className?: string
}

/**
 * What a table row becomes below 640. The design system is explicit about the
 * three things that survive the collapse — id, status and amount — so this
 * takes exactly those rather than a generic row renderer.
 */
export function RecordCard({ id, status, title, amount, onClick, className }: RecordCardProps) {
  const Element = onClick === undefined ? 'div' : 'button'

  return (
    <Element
      type={onClick === undefined ? undefined : 'button'}
      onClick={onClick}
      data-slot="record-card"
      className={cn(
        'w-full rounded-xl border border-veil/7 bg-veil/4 px-3 py-2.75 text-left',
        // `press-wide`, not `press`: this is a table row, as wide as the
        // column. 3% of that is tens of pixels of travel, which reads as the
        // card sliding rather than being pushed.
        onClick !== undefined && 'press-wide hover:bg-surface-2',
        className,
      )}
    >
      <div className="mb-1.75 flex items-center gap-2">
        <span className="font-mono text-caption text-fg-body">{id}</span>
        {status !== undefined && (
          <span className="ml-auto">
            <StatusBadge tone={status.tone} size="sm">
              {status.label}
            </StatusBadge>
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 truncate text-body text-fg-secondary">{title}</span>
        {amount !== undefined && (
          <span className="ml-auto shrink-0 font-mono text-caption whitespace-nowrap text-foreground">
            {amount}
          </span>
        )}
      </div>
    </Element>
  )
}
